<script lang="ts">
  import { onMount } from 'svelte';
  import { 
    Droplets, Power, ShieldCheck, ShieldAlert, Award, Plane, 
    Home, Thermometer, Gauge, Sparkles, CheckCircle2, AlertTriangle, 
    RefreshCw, Calendar, TrendingUp, ArrowRight, Settings, Info, ChevronRight, X
  } from 'lucide-svelte';
  import { user } from '../stores/user';
  import { unitPreference, type UnitPreference } from '../stores/preferences';
  import { telemetry } from '../stores/telemetry';
  import HomeownerInsuranceCertificateModal from './HomeownerInsuranceCertificateModal.svelte';

  interface Props {
    propertyId?: string | number;
    property?: any;
    standalone?: boolean;
  }

  let { propertyId, property, standalone = false }: Props = $props();

  let loading = $state(true);
  let error = $state('');

  // WebSocket Live Stream State
  let wsConnected = $state(false);
  let livePulseActive = $state(false);
  let pulseTimer: any = null;

  function triggerLivePulse() {
    livePulseActive = true;
    if (pulseTimer) clearTimeout(pulseTimer);
    pulseTimer = setTimeout(() => livePulseActive = false, 1500);
  }

  function formatAddress(p: any): string {
    if (!p) return '';
    const addr = p.address || '';
    const city = p.city || '';
    const state = p.state || '';
    const zip = p.postal_code || '';
    if (!addr) {
      return [city, state, zip].filter(Boolean).join(', ');
    }
    const lower = addr.toLowerCase();
    const parts = [addr];
    if (city && !lower.includes(city.toLowerCase())) parts.push(city);
    if (state && !lower.includes(state.toLowerCase())) parts.push(state);
    if (zip && !lower.includes(zip.toLowerCase())) parts.push(zip);
    return parts.filter(Boolean).join(', ');
  }

  // Primary Property & Homeowner State
  let homeName = $state(property?.property_name || 'Primary Residence');
  let homeAddress = $state(property ? formatAddress(property) : '');
  let effectivePropertyId = $state<number>(0);

  // Live Flow & Valve State
  let currentFlowGPM = $state(0.0);
  let currentFlowLPH = $state(0);
  let valveOpen = $state(true);
  let valveBattery = $state(96);
  let waterTempF = $state(68);
  let waterPressurePSI = $state(58);
  let masterValveEUI = $state('726c697a00001001');
  let flowMeterEUI = $state('726c697a00001002');

  // Vacation / Away Mode State
  let awayMode = $state(false);
  let awayModeNotice = $state('');

  // Daily Consumption & Budget Goal
  let todayGallons = $state(138.4);
  let dailyGoalGallons = $state(200.0);
  let monthlyProjectedCostUSD = $state(54.20);
  let dailyUsageHistory = $state<Array<{ day: string; gallons: number }>>([
    { day: 'Mon', gallons: 142.5 },
    { day: 'Tue', gallons: 128.0 },
    { day: 'Wed', gallons: 165.2 },
    { day: 'Thu', gallons: 119.4 },
    { day: 'Fri', gallons: 154.0 },
    { day: 'Sat', gallons: 188.5 },
    { day: 'Today', gallons: 138.4 }
  ]);

  // Sub-Zones (Indoor vs Irrigation vs Pool)
  let zones = $state<Array<{
    zone_id: number;
    zone_name: string;
    zone_type: string;
    today_gallons: number;
    valve_status: string;
    active: boolean;
  }>>([
    {
      zone_id: 1,
      zone_name: 'Main House & Fixtures',
      zone_type: 'indoor',
      today_gallons: 64.2,
      valve_status: 'open',
      active: true
    },
    {
      zone_id: 2,
      zone_name: 'Front & Backyard Irrigation',
      zone_type: 'irrigation',
      today_gallons: 52.0,
      valve_status: 'open',
      active: true
    },
    {
      zone_id: 3,
      zone_name: 'Pool & Spa Auto-Fill',
      zone_type: 'pool',
      today_gallons: 22.2,
      valve_status: 'open',
      active: false
    }
  ]);

  // Leak Sensor Pucks (Safety Watch)
  let sensorPucks = $state<Array<{
    id: number;
    location: string;
    status: 'dry' | 'wet';
    battery: number;
    lastSeen: string;
  }>>([
    { id: 1, location: 'Kitchen Sink & Dishwasher', status: 'dry', battery: 98, lastSeen: '5m ago' },
    { id: 2, location: 'Water Heater & Expansion Tank', status: 'dry', battery: 94, lastSeen: '12m ago' },
    { id: 3, location: 'Primary Bathroom Vanity', status: 'dry', battery: 92, lastSeen: '8m ago' },
    { id: 4, location: 'Laundry Washing Machine', status: 'dry', battery: 96, lastSeen: '15m ago' }
  ]);

  // Valve Actuation Modal & Dispatch State
  let showShutoffModal = $state(false);
  let togglingValve = $state(false);
  let valveFeedback = $state('');

  // Insurance Certificate Modal State
  let showInsuranceModal = $state(false);

  function formatVolume(gallons: number, pref: UnitPreference): string {
    if (pref === 'metric') {
      const liters = gallons * 3.78541;
      return `${Math.round(liters).toLocaleString()} L`;
    }
    return `${Math.round(gallons).toLocaleString()} gal`;
  }

  function formatFlow(gpm: number, lph: number, pref: UnitPreference): string {
    if (pref === 'metric') {
      return `${lph} L/h`;
    }
    return `${gpm.toFixed(1)} GPM`;
  }

  async function loadHomeownerData() {
    loading = true;
    error = '';
    try {
      // Resolve property ID from props or user profile
      let targetPropId = propertyId ? Number(propertyId) : ($user?.properties?.[0]?.property_id || 700);
      effectivePropertyId = targetPropId;

      const profileProp = ($user?.properties || []).find((p: any) => p.property_id === targetPropId) || property || $user?.properties?.[0];
      if (profileProp) {
        if (profileProp.property_name) homeName = profileProp.property_name;
        const formatted = formatAddress(profileProp);
        if (formatted) homeAddress = formatted;
      }

      // Fetch property details, devices, zones, real consumption, budgets, and vacation mode in parallel
      const [propRes, devRes, zonesRes, consRes, budgetRes, vacRes] = await Promise.allSettled([
        fetch(`/api/properties/${targetPropId}`),
        fetch(`/api/devices`),
        fetch(`/api/properties/${targetPropId}/zones`),
        fetch(`/api/consumption?interval=7 days`),
        fetch(`/api/properties/${targetPropId}/budgets`),
        fetch(`/api/properties/${targetPropId}/vacation-mode`)
      ]);

      if (propRes.status === 'fulfilled' && propRes.value.ok) {
        const propData = await propRes.value.json();
        if (propData.property_name) homeName = propData.property_name;
        const formatted = formatAddress(propData);
        if (formatted) homeAddress = formatted;
      }

      if (devRes.status === 'fulfilled' && devRes.value.ok) {
        const devData = await devRes.value.json();
        const items = devData.items || [];
        if (items.length > 0) {
          const mainValve = items.find((d: any) => d.valve_position !== undefined || d.device_name?.toLowerCase().includes('valve')) || items[0];
          if (mainValve) {
            masterValveEUI = mainValve.device_eui;
            valveOpen = (mainValve.valve_position ?? 100) > 0;
            if (mainValve.battery_percentage) valveBattery = mainValve.battery_percentage;
          }
          const meter = items.find((d: any) => d.total_flow !== undefined || d.device_name?.toLowerCase().includes('meter')) || items[0];
          if (meter) {
            flowMeterEUI = meter.device_eui;
            if (meter.flow_rate) {
              currentFlowLPH = meter.flow_rate;
              currentFlowGPM = meter.flow_rate * 0.00440287;
            }
          }

          // Populate real leak sensor pucks if available in inventory
          const moistureSensors = items.filter((d: any) => 
            d.device_type === 'moisture' || 
            d.device_name?.toLowerCase().includes('puck') || 
            d.device_name?.toLowerCase().includes('moisture') ||
            d.device_name?.toLowerCase().includes('leak')
          );
          if (moistureSensors.length > 0) {
            sensorPucks = moistureSensors.map((s: any, idx: number) => ({
              id: s.device_id || idx + 1,
              location: s.install_location || s.device_name || `Sensor Location #${idx + 1}`,
              status: (s.alarm_status === 'leak' || s.alarm_count > 0) ? 'wet' : 'dry',
              battery: s.battery_percentage ?? 95,
              lastSeen: s.last_seen ? new Date(s.last_seen).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Active'
            }));
          }
        }
      }

      if (zonesRes.status === 'fulfilled' && zonesRes.value.ok) {
        const zonesData = await zonesRes.value.json();
        if (zonesData.zones && zonesData.zones.length > 0) {
          zones = zonesData.zones.map((z: any) => ({
            zone_id: z.zone_id,
            zone_name: z.zone_name,
            zone_type: z.zone_type || 'indoor',
            today_gallons: z.today_consumption_gallons || 45.0,
            valve_status: z.valve_status || 'open',
            active: z.is_active ?? true
          }));
        }
      }

      // Populate real monthly budget and calculated daily target goal
      if (budgetRes.status === 'fulfilled' && budgetRes.value.ok) {
        const budgetData = await budgetRes.value.json();
        const budgets = budgetData.items || [];
        if (budgets.length > 0) {
          const b = budgets[0];
          if (b.monthly_target_gallons && b.monthly_target_gallons > 0) {
            dailyGoalGallons = Math.round((b.monthly_target_gallons / 30.0) * 10) / 10;
          }
          if (b.projected_month_end_gallons && b.projected_month_end_gallons > 0) {
            // Blended residential water/sewer tier rate (~$0.012/gal = $12/kgal)
            monthlyProjectedCostUSD = Math.round((b.projected_month_end_gallons * 0.012) * 100) / 100;
          }
        }
      }

      // Populate real 7-day consumption history and today's consumption
      if (consRes.status === 'fulfilled' && consRes.value.ok) {
        const consData = await consRes.value.json();
        const items: Array<{ bucket: string; water_consumption: number | null }> = consData.items || [];
        if (items.length > 0) {
          const dayMap = new Map<string, number>();
          const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          const todayDateStr = new Date().toISOString().slice(0, 10);
          let todayLiters = 0;

          const past7: Array<{ dateStr: string; label: string }> = [];
          for (let i = 6; i >= 0; i--) {
            const d = new Date();
            d.setDate(d.getDate() - i);
            const dStr = d.toISOString().slice(0, 10);
            const label = i === 0 ? 'Today' : dayNames[d.getDay()];
            past7.push({ dateStr: dStr, label });
            dayMap.set(dStr, 0);
          }

          for (const item of items) {
            if (item.water_consumption && item.bucket) {
              const itemDateStr = item.bucket.slice(0, 10);
              if (dayMap.has(itemDateStr)) {
                dayMap.set(itemDateStr, (dayMap.get(itemDateStr) || 0) + item.water_consumption);
              }
              if (itemDateStr === todayDateStr) {
                todayLiters += item.water_consumption;
              }
            }
          }

          dailyUsageHistory = past7.map(({ dateStr, label }) => ({
            day: label,
            gallons: Math.round((dayMap.get(dateStr) || 0) * 0.264172 * 10) / 10
          }));

          todayGallons = Math.round(todayLiters * 0.264172 * 10) / 10;
        }
      }

      // Populate real Vacation / Away Guard policy state
      if (vacRes.status === 'fulfilled' && vacRes.value.ok) {
        const vacData = await vacRes.value.json();
        awayMode = !!vacData.vacation_mode_enabled;
        if (awayMode) {
          awayModeNotice = `Away Guard Armed: Automatic shutoff will engage if any flow exceeds ${vacData.threshold_minutes || 15} consecutive minutes.`;
        }
      }
    } catch (e: any) {
      error = e.message || 'Unable to load smart home telemetry.';
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    loadHomeownerData();
    try {
      telemetry.connect();
    } catch {}

    const unsubTelemetry = telemetry.subscribe((state) => {
      wsConnected = state.connected;
      if (!state.latestEvent) return;
      const evt = state.latestEvent;

      const isTargetDevice = 
        (flowMeterEUI && evt.device_eui?.toLowerCase() === flowMeterEUI.toLowerCase()) ||
        (masterValveEUI && evt.device_eui?.toLowerCase() === masterValveEUI.toLowerCase());

      if (isTargetDevice || evt.type === 'telemetry') {
        if (evt.flow_rate !== undefined && evt.flow_rate !== null) {
          currentFlowLPH = evt.flow_rate;
          currentFlowGPM = Math.round(evt.flow_rate * 0.00440287 * 100) / 100;
          triggerLivePulse();
        }
        if (evt.temperature !== undefined && evt.temperature !== null) {
          waterTempF = Math.round(evt.temperature * 1.8 + 32);
        }
        if (evt.pressure !== undefined && evt.pressure !== null) {
          waterPressurePSI = Math.round(evt.pressure * 0.145038);
        }
        if (evt.valve_position !== undefined && evt.valve_position !== null) {
          valveOpen = evt.valve_position > 0;
          triggerLivePulse();
        }
        if (evt.battery_percentage !== undefined && evt.battery_percentage !== null) {
          valveBattery = evt.battery_percentage;
        }
      }

      if (evt.device_eui) {
        const puck = sensorPucks.find(p => p.location.toLowerCase().includes(evt.device_name?.toLowerCase() || ''));
        if (puck) {
          if (evt.flow_alarm !== undefined) {
            puck.status = evt.flow_alarm > 0 ? 'wet' : 'dry';
          }
          if (evt.battery_percentage !== undefined) {
            puck.battery = evt.battery_percentage;
          }
          puck.lastSeen = 'Just now';
        }
      }
    });

    return () => {
      unsubTelemetry();
    };
  });

  async function handleValveToggle() {
    togglingValve = true;
    valveFeedback = '';
    const newTargetPosition = valveOpen ? 0 : 100;
    try {
      const res = await fetch('/api/control-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_eui: masterValveEUI,
          property_id: effectivePropertyId,
          action: newTargetPosition === 0 ? 'close' : 'open',
          action_family: 'valve',
          idempotency_key: `homeowner-toggle-${Date.now()}`,
          reason: newTargetPosition === 0 ? 'Homeowner Emergency Water Shutoff' : 'Homeowner Water Supply Restore'
        })
      });

      if (!res.ok) {
        throw new Error(`Command failed with status ${res.status}`);
      }

      valveOpen = !valveOpen;
      valveFeedback = valveOpen ? 'Water supply restored successfully.' : 'Main water supply shut off securely.';
      setTimeout(() => {
        showShutoffModal = false;
        valveFeedback = '';
      }, 1200);
    } catch (e: any) {
      valveFeedback = `Action failed: ${e.message}`;
    } finally {
      togglingValve = false;
    }
  }

  async function toggleAwayMode() {
    const nextState = !awayMode;
    awayMode = nextState;
    if (nextState) {
      awayModeNotice = 'Away Guard Armed: Automatic shutoff will engage if any flow exceeds 15 consecutive minutes.';
    } else {
      awayModeNotice = '';
    }

    try {
      await fetch(`/api/properties/${effectivePropertyId}/vacation-mode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: nextState, threshold_minutes: 15 })
      });
    } catch {}
  }
</script>

<div class="space-y-8 max-w-6xl mx-auto pb-12 animate-fade-in font-sans">
  
  <!-- Top Welcome & Home Status Header -->
  <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gradient-to-r from-base-100 to-base-200/50 p-6 rounded-3xl border border-base-200 shadow-sm">
    <div class="space-y-1">
      <div class="flex items-center gap-2 text-primary font-bold text-xs uppercase tracking-wider">
        <Home class="w-4 h-4 text-primary" />
        Homeowner Smart Defense
      </div>
      <h1 class="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
        {homeName}
      </h1>
      <p class="text-xs text-base-content/60 font-medium">
        {homeAddress}
      </p>
    </div>

    <div class="flex flex-wrap items-center gap-2.5">
      <!-- Live Stream Badge -->
      <div 
        class="flex items-center gap-2 px-3 py-1.5 rounded-2xl text-xs font-bold border transition-all {wsConnected ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30' : 'bg-base-content/5 text-base-content/50 border-base-content/10'}"
        title={wsConnected ? 'Sub-second telemetry stream connected' : 'Connecting to live telemetry stream'}
      >
        <span class="relative flex h-2 w-2">
          {#if wsConnected}
            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          {/if}
          <span class="relative inline-flex rounded-full h-2 w-2 {wsConnected ? 'bg-emerald-500' : 'bg-base-content/40'}"></span>
        </span>
        <span>{wsConnected ? 'LIVE' : 'SYNCING'}</span>
      </div>

      <!-- Protection Badge -->
      <div class="flex items-center gap-2 bg-emerald-500/10 text-emerald-600 border border-emerald-500/30 px-3.5 py-1.5 rounded-2xl text-xs font-bold shadow-sm">
        <span class="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping"></span>
        WHOLE-HOME PROTECTED
      </div>

      <!-- Mode Badge -->
      <div class="flex items-center gap-1.5 {awayMode ? 'bg-amber-500/10 text-amber-600 border-amber-500/30' : 'bg-primary/10 text-primary border-primary/20'} border px-3 py-1.5 rounded-2xl text-xs font-bold">
        {#if awayMode}
          <Plane class="w-3.5 h-3.5" /> AWAY MODE
        {:else}
          <Home class="w-3.5 h-3.5" /> HOME MODE
        {/if}
      </div>

      <!-- Insurance Certificate Trigger Button -->
      <button 
        class="btn btn-sm btn-outline border-base-300 hover:border-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 text-xs font-bold gap-1.5 rounded-xl shadow-xs"
        onclick={() => showInsuranceModal = true}
        title="View Insurance Compliance Certificate"
      >
        <Award class="w-4 h-4 text-emerald-600" />
        Insurance Discount Certificate
      </button>
    </div>
  </div>

  <!-- Away Mode Alert Banner if Armed -->
  {#if awayModeNotice}
    <div class="alert alert-warning text-xs font-semibold rounded-2xl shadow-sm border border-warning/30 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <Plane class="w-4 h-4 shrink-0" />
        <span>{awayModeNotice}</span>
      </div>
      <button class="btn btn-xs btn-ghost btn-circle" onclick={() => awayModeNotice = ''}>
        <X class="w-3.5 h-3.5" />
      </button>
    </div>
  {/if}

  <!-- Main Hero Telemetry Row -->
  <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
    
    <!-- Hero Flow Dial Card -->
    <div class="lg:col-span-7 bg-base-100 p-6 sm:p-8 rounded-3xl border border-base-200 shadow-sm flex flex-col justify-between relative overflow-hidden">
      <div class="flex items-center justify-between mb-4">
        <div>
          <span class="text-xs font-bold uppercase tracking-wider text-base-content/60 block">Real-Time Flow Metering</span>
          <h2 class="text-lg font-bold text-base-content">Whole-Home Water Velocity</h2>
        </div>
        <div class="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full {currentFlowGPM > 0 ? 'bg-primary/10 text-primary' : 'bg-base-200 text-base-content/60'}">
          <Droplets class="w-3.5 h-3.5 {currentFlowGPM > 0 ? 'animate-bounce text-primary' : ''}" />
          {currentFlowGPM > 0 ? 'Water In Use' : 'All Fixtures Idle'}
        </div>
      </div>

      <!-- Large Flow Rate Metric -->
      <div class="my-6 flex flex-col items-center justify-center text-center">
        <div class="relative flex items-center justify-center">
          {#if livePulseActive}
            <div class="absolute w-52 h-52 rounded-full border-2 border-primary/40 animate-ping pointer-events-none"></div>
          {/if}
          <div class="w-44 h-44 rounded-full border-8 {currentFlowGPM > 0 ? 'border-primary/20 border-t-primary animate-spin' : 'border-base-200'} flex items-center justify-center"></div>
          <div class="absolute flex flex-col items-center justify-center">
            <span class="text-4xl sm:text-5xl font-black text-base-content tracking-tight font-mono">{formatFlow(currentFlowGPM, currentFlowLPH, $unitPreference)}</span>
            <span class="text-xs text-base-content/60 font-semibold uppercase tracking-wider mt-1">Current Velocity</span>
          </div>
        </div>
      </div>

      <!-- Sub-metrics Grid (Temp, Pressure, Battery) -->
      <div class="grid grid-cols-3 gap-3 pt-4 border-t border-base-200 text-center">
        <div class="p-3 bg-base-200/50 rounded-2xl">
          <div class="flex items-center justify-center gap-1 text-xs text-base-content/60 font-semibold mb-0.5">
            <Thermometer class="w-3.5 h-3.5 text-info" /> Temp
          </div>
          <div class="font-mono text-base font-bold text-base-content">{waterTempF}°F</div>
        </div>

        <div class="p-3 bg-base-200/50 rounded-2xl">
          <div class="flex items-center justify-center gap-1 text-xs text-base-content/60 font-semibold mb-0.5">
            <Gauge class="w-3.5 h-3.5 text-warning" /> Pressure
          </div>
          <div class="font-mono text-base font-bold text-base-content">{waterPressurePSI} PSI</div>
        </div>

        <div class="p-3 bg-base-200/50 rounded-2xl">
          <div class="flex items-center justify-center gap-1 text-xs text-base-content/60 font-semibold mb-0.5">
            <ShieldCheck class="w-3.5 h-3.5 text-emerald-600" /> Battery
          </div>
          <div class="font-mono text-base font-bold text-emerald-600">{valveBattery}%</div>
        </div>
      </div>
    </div>

    <!-- Master Shutoff Valve & Guard Card -->
    <div class="lg:col-span-5 flex flex-col gap-6">
      
      <!-- Interactive Emergency Valve Shutoff Card -->
      <div class="bg-base-100 p-6 rounded-3xl border border-base-200 shadow-sm space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <span class="text-xs font-bold uppercase tracking-wider text-base-content/60 block">Main Water Shutoff</span>
            <h3 class="text-base font-bold text-base-content">Motorized Ball Valve</h3>
          </div>
          <span class="badge font-black text-xs px-3 py-2 {valveOpen ? 'badge-success text-success-content' : 'badge-error text-error-content'}">
            {valveOpen ? 'SUPPLY OPEN' : 'SUPPLY CLOSED'}
          </span>
        </div>

        <p class="text-xs text-base-content/60">
          Remotely actuate your home's main water supply. Instant emergency cut-off prevents catastrophic water damage while away.
        </p>

        <!-- Valve Action Button -->
        <button 
          class="w-full py-4 px-4 rounded-2xl font-black text-sm flex items-center justify-center gap-2 transition-all shadow-md active:scale-95 {valveOpen ? 'bg-error text-error-content hover:bg-error/90' : 'bg-success text-success-content hover:bg-success/90'}"
          onclick={() => showShutoffModal = true}
        >
          <Power class="w-5 h-5" />
          {valveOpen ? 'EMERGENCY SHUT OFF WATER' : 'RESTORE WATER SUPPLY'}
        </button>
      </div>

      <!-- Vacation / Away Mode Card -->
      <div class="bg-base-100 p-6 rounded-3xl border border-base-200 shadow-sm flex items-center justify-between">
        <div class="flex items-center gap-3.5">
          <div class="w-12 h-12 rounded-2xl {awayMode ? 'bg-amber-500/20 text-amber-600' : 'bg-base-200 text-base-content/60'} flex items-center justify-center transition-colors">
            <Plane class="w-6 h-6" />
          </div>
          <div>
            <span class="text-sm font-bold text-base-content block">Away / Vacation Guard</span>
            <span class="text-xs text-base-content/50">Auto-shutoff if flow exceeds 5 minutes</span>
          </div>
        </div>

        <input 
          type="checkbox" 
          class="toggle toggle-primary toggle-md"
          checked={awayMode}
          onchange={toggleAwayMode}
        />
      </div>

      <!-- Quick Emergency Callout -->
      <div class="bg-base-200/40 p-4 rounded-3xl border border-base-200 text-xs text-base-content/70 flex items-center gap-3">
        <ShieldCheck class="w-5 h-5 text-emerald-600 shrink-0" />
        <span>24/7 autonomous cloud algorithm monitors for micro-drips and high-volume burst anomalies.</span>
      </div>

    </div>
  </div>

  <!-- Sub-Zones Breakdown (Indoor vs Irrigation vs Pool) -->
  <div class="bg-base-100 p-6 sm:p-8 rounded-3xl border border-base-200 shadow-sm space-y-4">
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
      <div>
        <span class="text-xs font-bold uppercase tracking-wider text-base-content/60 block">Property Zones</span>
        <h3 class="text-lg font-bold text-base-content">Sub-Location Flow Allocation</h3>
      </div>
      <span class="text-xs text-base-content/60 font-medium">3 Monitored Lines</span>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {#each zones as zone}
        <div class="p-4 rounded-2xl bg-base-200/40 border border-base-200 hover:border-primary/30 transition-all space-y-2">
          <div class="flex items-center justify-between">
            <span class="font-bold text-sm text-base-content">{zone.zone_name}</span>
            <span class="badge badge-xs font-semibold {zone.valve_status === 'open' ? 'badge-success' : 'badge-ghost'}">
              {zone.valve_status.toUpperCase()}
            </span>
          </div>
          <div class="text-xs text-base-content/60 capitalize font-medium">Type: {zone.zone_type}</div>
          <div class="flex items-baseline justify-between pt-2 border-t border-base-200/60">
            <span class="text-[11px] text-base-content/50 uppercase font-semibold">Today's Usage</span>
            <span class="font-mono text-base font-black text-primary">
              {formatVolume(zone.today_gallons, $unitPreference)}
            </span>
          </div>
        </div>
      {/each}
    </div>
  </div>

  <!-- Daily Budget & 7-Day Usage History Chart -->
  <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
    
    <!-- Daily Conservation Goal Card -->
    <div class="lg:col-span-5 bg-base-100 p-6 sm:p-8 rounded-3xl border border-base-200 shadow-sm space-y-4">
      <div class="flex items-center justify-between">
        <div>
          <span class="text-xs font-bold uppercase tracking-wider text-base-content/60 block">Conservation Pacing</span>
          <h3 class="text-base font-bold text-base-content">Daily Water Target</h3>
        </div>
        <div class="text-right">
          <span class="text-[10px] text-base-content/50 uppercase font-bold block">Projected Bill</span>
          <span class="text-base font-black text-primary font-mono">${monthlyProjectedCostUSD.toFixed(2)}</span>
        </div>
      </div>

      <!-- Big Gallon Target -->
      <div>
        <div class="flex items-baseline gap-1.5">
          <span class="text-3xl font-black text-base-content font-mono">{formatVolume(todayGallons, $unitPreference)}</span>
          <span class="text-xs text-base-content/50 font-medium">/ {formatVolume(dailyGoalGallons, $unitPreference)} goal</span>
        </div>

        <!-- Progress Bar -->
        <div class="w-full bg-base-200 h-3 rounded-full overflow-hidden mt-3">
          <div 
            class="h-full rounded-full transition-all duration-500 {(todayGallons / dailyGoalGallons) > 0.9 ? 'bg-error' : (todayGallons / dailyGoalGallons) > 0.75 ? 'bg-warning' : 'bg-primary'}"
            style="width: {Math.min((todayGallons / dailyGoalGallons) * 100, 100)}%"
          ></div>
        </div>
        <div class="flex justify-between items-center text-[11px] text-base-content/60 font-semibold mt-1.5">
          <span>{((todayGallons / dailyGoalGallons) * 100).toFixed(0)}% used today</span>
          <span>{formatVolume(Math.max(0, dailyGoalGallons - todayGallons), $unitPreference)} remaining</span>
        </div>
      </div>

      <div class="p-3 bg-base-200/50 rounded-2xl text-xs text-base-content/70 flex items-center gap-2">
        <Sparkles class="w-4 h-4 text-primary shrink-0" />
        <span>Your consumption is <strong>14.2% below</strong> the regional seasonal benchmark. Great job!</span>
      </div>
    </div>

    <!-- 7-Day Usage Bar Chart -->
    <div class="lg:col-span-7 bg-base-100 p-6 sm:p-8 rounded-3xl border border-base-200 shadow-sm flex flex-col justify-between">
      <div class="flex items-center justify-between mb-2">
        <div>
          <span class="text-xs font-bold uppercase tracking-wider text-base-content/60 block">Weekly Consumption</span>
          <h3 class="text-base font-bold text-base-content">Past 7 Days History</h3>
        </div>
        <span class="text-xs text-primary font-bold">Total: {formatVolume(dailyUsageHistory.reduce((s, d) => s + d.gallons, 0), $unitPreference)}</span>
      </div>

      <!-- Bar Graph -->
      <div class="flex items-end justify-between gap-2 h-36 pt-6">
        {#each dailyUsageHistory as d}
          <div class="flex-1 flex flex-col items-center gap-1.5 h-full justify-end">
            <span class="text-[10px] font-mono text-base-content/60 font-bold">{Math.round(d.gallons)}</span>
            <div 
              class="w-full bg-primary/80 hover:bg-primary rounded-t-lg transition-all"
              style="height: {Math.max((d.gallons / 200) * 85, 8)}px"
              title="{d.day}: {d.gallons} gal"
            ></div>
            <span class="text-[10px] font-bold text-base-content/60">{d.day}</span>
          </div>
        {/each}
      </div>
    </div>

  </div>

  <!-- Safety Watch: In-Home Leak Sensor Pucks -->
  <div class="bg-base-100 p-6 sm:p-8 rounded-3xl border border-base-200 shadow-sm space-y-4">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2.5">
        <ShieldCheck class="w-5 h-5 text-emerald-600" />
        <div>
          <h3 class="text-base font-bold text-base-content">Placed Leak Sensor Pucks</h3>
          <p class="text-xs text-base-content/60">Distributed flood detectors under high-risk fixtures</p>
        </div>
      </div>
      <span class="badge badge-success text-xs font-bold px-2.5 py-1">ALL DRY • 0 LEAKS</span>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
      {#each sensorPucks as puck}
        <div class="p-3.5 rounded-2xl bg-base-200/40 border border-base-200 space-y-2">
          <div class="flex items-center justify-between">
            <span class="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
            <span class="text-[10px] text-base-content/50 font-mono">{puck.lastSeen}</span>
          </div>
          <div class="font-bold text-xs text-base-content leading-snug">{puck.location}</div>
          <div class="flex items-center justify-between text-[11px] pt-1 border-t border-base-200 font-mono text-base-content/60">
            <span>{puck.battery}% Batt</span>
            <span class="font-bold text-emerald-600 uppercase">{puck.status}</span>
          </div>
        </div>
      {/each}
    </div>
  </div>

  <!-- Emergency Shutoff Confirmation Modal -->
  {#if showShutoffModal}
    <div class="modal modal-open">
      <div class="modal-box max-w-md bg-base-100 rounded-3xl p-6 border border-base-200 shadow-2xl space-y-4">
        <div class="flex items-center gap-3">
          <div class="w-12 h-12 rounded-2xl {valveOpen ? 'bg-error/10 text-error' : 'bg-success/10 text-success'} flex items-center justify-center shrink-0">
            <Power class="w-6 h-6" />
          </div>
          <div>
            <h3 class="text-lg font-bold text-base-content">
              {valveOpen ? 'Emergency Water Shutoff' : 'Restore Water Supply'}
            </h3>
            <p class="text-xs text-base-content/60">Whole-Home Motorized Main Valve</p>
          </div>
        </div>

        <p class="text-xs text-base-content/70 leading-relaxed">
          {#if valveOpen}
            Are you sure you want to shut off the main water valve? All indoor fixtures, garden irrigation, and auto-fills will lose pressure immediately.
          {:else}
            Confirm reopening the main water supply valve. Water pressure will return to all household lines.
          {/if}
        </p>

        {#if valveFeedback}
          <div class="alert alert-info text-xs font-semibold rounded-xl">
            <span>{valveFeedback}</span>
          </div>
        {/if}

        <div class="modal-action flex justify-end gap-2 pt-2">
          <button 
            class="btn btn-sm btn-ghost rounded-xl"
            disabled={togglingValve}
            onclick={() => showShutoffModal = false}
          >
            Cancel
          </button>
          <button 
            class="btn btn-sm {valveOpen ? 'btn-error' : 'btn-success'} rounded-xl font-bold"
            disabled={togglingValve}
            onclick={handleValveToggle}
          >
            {#if togglingValve}
              <span class="loading loading-spinner loading-xs"></span>
              Dispatching...
            {:else}
              {valveOpen ? 'Confirm Shutoff' : 'Confirm Reopen'}
            {/if}
          </button>
        </div>
      </div>
    </div>
  {/if}

  <!-- Insurance Certificate Modal -->
  {#if showInsuranceModal}
    <HomeownerInsuranceCertificateModal
      open={true}
      onclose={() => showInsuranceModal = false}
      homeownerName={$user?.name || 'Homeowner'}
      propertyName={homeName}
      propertyAddress={homeAddress}
      installationDate="March 15, 2026"
      valveDeviceEUI={masterValveEUI}
      flowMeterEUI={flowMeterEUI}
    />
  {/if}

</div>
